import { Component, type ErrorInfo, type ReactNode } from 'react';
import i18n from '../../../lib/i18n/i18n';

/**
 * RootErrorBoundary
 * -----------------
 * The app lazy-loads every route (`React.lazy` + `<Suspense>`). Suspense only
 * handles the *pending* state — it does NOT catch errors. So with no boundary
 * above it, ANY of the following unmounts the whole React tree and leaves a
 * blank white page (the classic "refresh fixes it" bug):
 *
 *   1. A stale dynamic-import after a deploy: the browser is holding an old
 *      index.html whose hashed chunk URLs (e.g. DriversPage-abc123.js) no
 *      longer exist on the server → the import rejects → React.lazy throws.
 *   2. Any uncaught render error in a page component.
 *
 * This boundary catches both. For a chunk-load failure (almost always #1) it
 * does a one-shot hard reload to pull the fresh index.html + new chunk hashes
 * — which is exactly what the user was doing by hand. A sessionStorage guard
 * prevents a reload loop if the reload doesn't resolve it. For any other error
 * it shows a recoverable fallback instead of a blank screen.
 */

const RELOAD_GUARD_KEY = 'waypoint-chunk-reload-at';
const RELOAD_DEBOUNCE_MS = 10_000;

// Vite / browsers phrase dynamic-import failures a few different ways.
const CHUNK_ERROR_RE =
  /(Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Loading chunk \d+ failed|ChunkLoadError)/i;

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const name = (error as { name?: string }).name ?? '';
  const message = (error as { message?: string }).message ?? '';
  return name === 'ChunkLoadError' || CHUNK_ERROR_RE.test(message);
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class RootErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // Stale build after a deploy — pull fresh index.html + chunk hashes.
      // Guard against a reload loop if the fresh build still fails.
      let last = 0;
      try {
        last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
      } catch {
        /* sessionStorage may be unavailable */
      }
      const now = Date.now();
      if (now - last > RELOAD_DEBOUNCE_MS) {
        try {
          sessionStorage.setItem(RELOAD_GUARD_KEY, String(now));
        } catch {
          /* ignore */
        }
        window.location.reload();
        return;
      }
    }
    // Surface non-chunk errors for diagnostics; the fallback UI handles UX.
    console.error('[RootErrorBoundary] uncaught render error:', error, info.componentStack);
  }

  private handleReload = (): void => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-3 max-w-md text-center px-4">
          <p className="text-base font-medium text-gray-800">{i18n.t('components.rootErrorBoundary.title')}</p>
          <p className="text-sm text-gray-600">
            {i18n.t('components.rootErrorBoundary.body')}
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="mt-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            {i18n.t('components.rootErrorBoundary.reload')}
          </button>
        </div>
      </div>
    );
  }
}
